import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";
import { api } from "@/lib/api";

let teamCache = null, clauseCache = null;
export const useTeam = () => {
  const [t, setT] = useState(teamCache || []);
  useEffect(() => { if (!teamCache) api.get("/team").then((r) => { teamCache = r.data.users.filter((u) => u.status !== "deleted"); setT(teamCache); }).catch(() => {}); }, []);
  return t;
};
export const resetTeamCache = () => { teamCache = null; };
export const useClauses = () => {
  const [c, setC] = useState(clauseCache || []);
  useEffect(() => { if (!clauseCache) api.get("/clauses/options").then((r) => { clauseCache = r.data.filter((x) => !x.is_heading); setC(clauseCache); }).catch(() => {}); }, []);
  return c;
};

const NONE = "__none__";
export const SimpleSelect = ({ value, onChange, options, placeholder = "Select", testid, allowNone }) => (
  <Select value={value ? String(value) : ""} onValueChange={(v) => onChange(v === NONE ? "" : v)}>
    <SelectTrigger data-testid={testid} className="h-9"><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent className="max-h-72">
      {allowNone && <SelectItem value={NONE}>— None —</SelectItem>}
      {options.map((o) => { const [v, l] = Array.isArray(o) ? o : [o, o]; return <SelectItem key={v} value={String(v)}>{l}</SelectItem>; })}
    </SelectContent>
  </Select>
);

export const MultiCheck = ({ value = [], onChange, options, testid, cols = 2 }) => (
  <div className={`grid grid-cols-1 ${cols === 3 ? "sm:grid-cols-3" : "sm:grid-cols-2"} gap-1.5`} data-testid={testid}>
    {options.map((o) => (
      <label key={o} className="flex items-center gap-2 text-[13px]">
        <Checkbox checked={value.includes(o)} onCheckedChange={(c) => onChange(c ? [...value, o] : value.filter((x) => x !== o))} />{o}
      </label>
    ))}
  </div>
);

function Rows({ f, value = [], onChange }) {
  const team = useTeam(), clauses = useClauses();
  const upd = (i, k, v) => onChange(value.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <div className="space-y-2" data-testid={`field-${f.key}`}>
      {value.map((r, i) => (
        <div key={i} className="grid gap-2 p-2.5 rounded-md border bg-slate-50/60" style={{ gridTemplateColumns: `repeat(${Math.min(f.sub.length, 3)}, minmax(0,1fr)) auto` }}>
          {f.sub.map((s) => (
            <div key={s.key} className={s.wide ? "col-span-full" : ""}>
              <Label className="text-[11px] text-[#5A6B87]">{s.label}</Label>
              <Control f={s} value={r[s.key]} onChange={(v) => upd(i, s.key, v)} team={team} clauses={clauses} />
            </div>
          ))}
          <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} className="self-start p-1.5 text-slate-400 hover:text-[#D64545]"><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
      <Button type="button" size="sm" variant="outline" onClick={() => onChange([...value, {}])} data-testid={`add-row-${f.key}`}><Plus className="h-3.5 w-3.5 mr-1" />Add {f.rowLabel || "row"}</Button>
    </div>
  );
}

const SCALE = [1, 2, 3, 4, 5].map((n) => [n, `${n} - ${["Rare/Negligible", "Unlikely/Minor", "Possible/Moderate", "Likely/Major", "Almost certain/Severe"][n - 1]}`]);

function ClauseChecks({ value, onChange, clauses, tid }) {
  const v = value || [];
  return (
    <div className="max-h-40 overflow-y-auto border rounded-md p-2 space-y-1" data-testid={tid}>
      {clauses.map((x) => (
        <label key={x.id} className="flex items-center gap-2 text-xs"><Checkbox checked={v.includes(x.id)} onCheckedChange={(ch) => onChange(ch ? [...v, x.id] : v.filter((y) => y !== x.id))} /><span className="font-mono">{x.clause_no}</span> {x.title}</label>
      ))}
    </div>
  );
}

function Control({ f, value, onChange, team, clauses }) {
  const tid = `field-${f.key}`;
  const ty = f.type;
  if (ty === "textarea") return <Textarea rows={f.rows || 3} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={f.placeholder} data-testid={tid} />;
  if (ty === "select") return <SimpleSelect value={value} onChange={onChange} options={f.options} testid={tid} allowNone={f.allowNone} />;
  if (ty === "multi") return <MultiCheck value={value || []} onChange={onChange} options={f.options} testid={tid} cols={f.cols || 2} />;
  if (ty === "user") return <SimpleSelect value={value} onChange={onChange} options={team.map((u) => [u.id, `${u.name} · ${u.role_label}`])} testid={tid} allowNone placeholder="Select team member" />;
  if (ty === "clause") return <SimpleSelect value={value} onChange={onChange} options={clauses.map((x) => [x.id, `${x.clause_no} ${x.title}`])} testid={tid} allowNone placeholder="Select clause" />;
  if (ty === "clauses") return <ClauseChecks value={value} onChange={onChange} clauses={clauses} tid={tid} />;
  if (ty === "date") return <Input type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={tid} className="h-9" />;
  if (ty === "number") return <Input type="number" value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={tid} className="h-9" />;
  if (ty === "scale") return <SimpleSelect value={value} onChange={(x) => onChange(Number(x))} options={SCALE} testid={tid} />;
  return <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={f.placeholder} data-testid={tid} className="h-9" />;
}

export function Field({ f, value, onChange, team, clauses, compact }) {
  const el = f.type === "rows" ? <Rows f={f} value={value || []} onChange={onChange} /> : <Control f={f} value={value} onChange={onChange} team={team || []} clauses={clauses || []} />;
  return (
    <div className={`space-y-1.5 ${f.full && !compact ? "sm:col-span-2" : ""}`}>
      <Label className={compact ? "text-[11px] text-[#5A6B87]" : "text-[13px]"}>{f.label}</Label>
      {el}
      {f.help && <p className="text-[11px] text-[#5A6B87]">{f.help}</p>}
    </div>
  );
}
